import axios, { AxiosRequestConfig } from 'axios';
import type { IndexationInfo } from '../utils/indexationLabels';
import type { Droits, Oeuvre } from './perimetreFilesAPI';
import type { UserProfile } from '../types/userProfile';
import type { AssistantUsageInfo } from './assistantUsage';
import type { ChatCitation } from './chatAPI';
import type { ApercuConceptDto, FaceAFaceDto } from '../types/api';

/**
 * Organisations (C26 SP2, spec § 6) — le miroir client des routes
 * `/api/organisations` et de `POST /api/users/me/invitation` étendu (§ 7).
 * Les formes miroitent `api/src/services/OrganisationService.ts` et
 * `OrganisationContenusService.ts`. La porte est opaque : un 404 sur la fiche
 * veut dire « pas membre » (ou rôle insuffisant), jamais une erreur à afficher ;
 * tout autre refus porte un `code` et un `message` du serveur, relayés tels quels.
 */

export type RoleOrganisation = 'admin' | 'editeur';
export type ModeleOrganisation = 'loge-rer' | 'association' | 'vide';
export type StatutTravail = 'brouillon' | 'publie';
export type StatutMembre = 'actif' | 'suspendu';

/** « Qui peut lire » : absente = tous les membres ; `cercleMin` et `groupes` se cumulent (ET) ; `administrateurs` est exclusif (les administrateurs seuls). */
export interface AudienceOrganisation { cercleMin?: number; groupes?: string[]; administrateurs?: boolean }

export interface Cercle { ordre: number; cle: string; libelle: string }
export interface Groupe { cle: string; libelle: string }

export interface OrganisationResume {
    id: string; slug: string; nom: string; roles: RoleOrganisation[]; cercle: number; cercleLibelle: string;
}
export interface OrganisationFiche extends OrganisationResume {
    description?: string; modele: ModeleOrganisation; cercles: Cercle[]; groupes: Groupe[];
    reglages: { langue: 'fr' | 'en'; charte?: string; annuaireVisible: boolean; membresCommentent: boolean; accent?: string };
    /** `charteAAccepter` : la façade montre la charte avant l'assistant (C26 SP3). */
    moi: { roles: RoleOrganisation[]; cercle: number; cercleLibelle: string; groupes: string[]; charteAAccepter: boolean };
    recherchePrete: boolean;
    compte?: { membres: number; invitationsEnCours: number; plafondMembres?: number };
    /** Le contingent du mois de l'organisation (C26 SP3), des nombres seulement ; absent si illisible. */
    contingent?: AssistantUsageInfo;
}
export interface MembreDto {
    userId: string; displayName: string; cercle: number; cercleLibelle: string; groupes: string[];
    roles?: RoleOrganisation[]; statut?: StatutMembre; inviteLe?: string; actifLe?: string; suspenduLe?: string;
}
export interface InvitationDto {
    code: string; cercle: number; groupes: string[]; roles: Array<'editeur'>; expireLe?: string; max?: number; usages: number; createdAt: string;
}
/** La provenance d'un contenu versé depuis Anagogia (C26 SP4, spec § 5.3) ; `verseParNom` vaut '' si le profil a disparu. */
export interface VersementDto {
    depuis: { type: 'note' | 'concept' | 'dossier'; id: string; titre: string };
    auteurNom: string; verseParNom: string; verseLe: string;
}
export interface DossierDto { id: string; name: string; description?: string; audience?: AudienceOrganisation }
export type Consultation = 'lecture' | 'interrogation';
export interface DocumentDto {
    id: string; displayName: string; mimeType: string; size?: number; perimetreIds: string[]; audience?: AudienceOrganisation;
    indexation?: IndexationInfo; url?: string;
    /** « Interrogeable seulement » : le document répond par extraits courts et ne s'ouvre pas aux membres (C26 SP4 bis). */
    consultation: Consultation;
    /** L'œuvre derrière le document et la déclaration de droits de l'organisation (C27 SP6), si présentes. */
    oeuvre?: Oeuvre; droits?: Droits;
    versement?: VersementDto;
}
export interface TravailDto {
    id: string; titre: string; statut: StatutTravail; audience?: AudienceOrganisation;
    perimetres: Array<{ id: string; name: string }>; updatedDate: string; extrait: string;
    versement?: VersementDto;
}
export interface ConceptDto { id: string; cle: string; definition?: string; versement?: VersementDto }
export interface VocabulaireDto { id: string | null; nom: string; concepts: ConceptDto[] }
export interface EtatIndexationDto { id: string; displayName: string; indexation?: IndexationInfo }
export interface DepotPrepare { fileId: string; uploadUrl: string; displayName: string }
export interface LotIndexationCree { jobId: string; resolvedCount: number; status: string }

export interface CreerOrganisationBody { nom: string; slug?: string; modele: ModeleOrganisation; codeParrainage?: string }

// ── C26 SP5 : l'assistant de l'organisation (forfait, contingent, clé, abonnement, parrainage) ──
export type PalierOrganisation = 'decouverte' | 'plus';
export interface AbonnementDto { disponible: boolean; checkout: boolean; portal: boolean }
export interface CleOrganisationDto { provider: string; model: string; maskedKey: string; temperature?: number }
export interface AssistantOrganisationDto {
    forfait: { tier: PalierOrganisation; label: string; source: 'manual' | 'stripe' | 'aucune'; validUntil?: string };
    contingent: { limit: number; used: number; resetsOn: string; credits?: number };
    limites: { maxMembres: number; maxFiles: number; maxStorageBytes: number; questionsPerMonth: number };
    usage: { membres: number; fichiers: number; octets: number; membresAyantDemande: number };
    cle: CleOrganisationDto | null;
    cleAutorisee: boolean;
    abonnement: AbonnementDto;
    parrainage: { code: string; filleuls: number; creditsDuMois: number };
}
// ── C26 SP6 : le suivi de l'organisation (administrateur), en nombres seulement ──
export type PeriodeSuivi = 'semaine' | 'mois' | 'debut';
/**
 * Ce que rend `GET …/suivi` (spec SP6 § 4, § 5.6) : les neuf blocs déjà agrégés, noms et titres
 * résolus, aucun identifiant de personne. Recopié champ pour champ d'`api/src/services/OrganisationSuiviService.ts`.
 */
export interface SuiviOrganisationDto {
    /** Premier jour compté ; `null` = aucun compteur encore. */
    depuis: string | null;
    semaine: number | null;
    periode: PeriodeSuivi;
    actifs: { actifs: number; total: number; jamais: number };
    questions: { n: number; ecart: number | null };
    sansSource: { n: number; sur: number };
    passages: { ouverts: number; ouvrables: number };
    /** Huit semaines, du plus ancien au courant ; hors sélecteur. */
    semaines: Array<{ lundi: string; questions: number; sansSource: number }>;
    /** Sur les huit semaines. */
    appareils: { telephone: number; ordinateur: number };
    /** Sur les huit semaines. */
    canaux: { azaria: number; anagogia: number };
    /** Membres actifs, du plus au moins, 0 compris. */
    parMembre: Array<{ nom: string; cercle: string; n: number }>;
    documents: {
        cites: Array<{ titre: string; n: number; interrogeableSeulement: boolean }>;
        /** Cinq titres au plus ; le reste en nombre. */
        jamaisCites: { titres: string[]; autres: number };
        plafonds: Array<{ titre: string; n: number }>;
    };
    usages: { passagesGardes: number; reponsesGardees: number; productions: number; anagogiaHorsAzaria: number };
    corpus: { interrogeables: number; enCours: number; illisibles: number };
}
export interface CleOrganisationBody { provider: string; apiKey?: string; model?: string; temperature?: number }
export interface SuppressionOrganisationResultat { complet: boolean; bilan: Array<{ libelle: string; genre: 'suppression' | 'detachement'; nombre: number; erreur?: string }> }
export interface ModifierOrganisationBody {
    nom?: string; description?: string; slug?: string;
    reglages?: { langue?: 'fr' | 'en'; charte?: string; annuaireVisible?: boolean; membresCommentent?: boolean; accent?: string | null };
}
export interface ModifierMembreBody { cercle?: number; groupes?: string[]; roles?: RoleOrganisation[]; statut?: StatutMembre }
export interface CreerInvitationBody { cercle?: number; groupes?: string[]; roles?: Array<'editeur'>; expireLe?: string; max?: number }
export interface DossierBody { name?: string; description?: string; audience?: AudienceOrganisation | null }
export interface DocumentBody { displayName?: string; perimetreId?: string; audience?: AudienceOrganisation | null; oeuvre?: Oeuvre | null; droits?: Droits | null; consultation?: Consultation }
export interface CreerTravailBody { titre?: string; texte?: string; perimetreId: string; audience?: AudienceOrganisation | null }
export interface ModifierTravailBody { titre?: string; statut?: StatutTravail; audience?: AudienceOrganisation | null; perimetreId?: string }
export interface ConceptBody { cle?: string; definition?: string }

/* ── Ponts avec Anagogia (C26 SP4) : miroir de `GarderService`, `VersementService` et du lot `verser-dossier` ── */

/** `POST …/garder/passage` : le passage sélectionné dans le lecteur (repères de lignes) ou retrouvé par son texte. */
export interface GarderPassageCorps {
    fileId: string; texte: string;
    page?: number; pageEnd?: number; chapter?: number; chapterLabel?: string; cfi?: string;
    lineStart?: number; lineEnd?: number; paragraphEstimate?: number;
    perimetreId?: string;
}
export interface GarderPassageResult {
    note: { id: string; title: string };
    perimetre: { id: string; name: string };
    /** `exact` : le passage est ancré dans le document ; `approche` : texte tel quel avec les repères de la citation. */
    repere: 'exact' | 'approche';
    /** Les concepts du vocabulaire de l'organisation reconnus dans le passage (dits dans le message de confirmation). */
    conceptsDeLOrganisation: string[];
}

export interface DejaGarde { fileId: string; excerpt: string; noteId: string }
/** `POST …/garder/reponse` : la réponse de l'assistant et ses citations (au plus 12). */
export interface GarderReponseCorps {
    question: string; texte: string;
    /** ISO 8601 : le moment de la réponse */
    le: string;
    citations: ChatCitation[];
    dejaGardes?: DejaGarde[];
    perimetreId?: string;
}
export interface GarderReponseResult {
    note: { id: string; title: string };
    /** Une note par citation de document : `index` = le repère [n] de la réponse, `reutilisee` = déjà gardée avant. */
    sources: Array<{ noteId: string; index: number; reutilisee: boolean }>;
    perimetre: { id: string; name: string };
    introuvables: number;
    /** Caractères de la réponse non gardés (au-delà de 12 000). */
    tronque: number;
}

/** `POST …/versements` : une note, un concept (avec les notes cochées), ou un dossier (lot). */
export type VerserCorps =
    | { type: 'note'; id: string; perimetreId: string; audience?: AudienceOrganisation | null }
    | { type: 'concept'; id: string; noteIds?: string[]; perimetreId?: string; audience?: AudienceOrganisation | null; dansExistant?: boolean }
    | { type: 'dossier'; id: string; avecDocuments?: boolean; audience?: AudienceOrganisation | null };
export interface VersementResult {
    cree: { type: 'note' | 'concept'; id: string; titre: string };
    travaux: Array<{ id: string; titre: string; depuis: string }>;
    passages: { reancres: number; references: number; introuvables: Array<{ noteId: string; extrait: string }> };
    marques: { conservees: number; retirees: number };
    documents?: { copies: number; enAttente: number };
}
/** Un dossier versé part en lot (202) : le bilan se lit sur `GET /api/bulk-jobs/{jobId}` et avance par `POST …/advance`. */
export interface VersementEnLot { jobId: string }
export const estVersementEnLot = (r: VersementResult | VersementEnLot): r is VersementEnLot => 'jobId' in r;

/** Réponse de `POST /api/users/me/invitation` : le profil, et l'organisation rejointe quand le code en est un. */
export interface AdhesionParCode { profile: UserProfile; organisation?: OrganisationResume }

/** Un refus du serveur, avec son code et son message affichable. */
export class OrganisationApiError extends Error {
    constructor(public readonly code: string, message: string, public readonly status?: number) {
        super(message);
        this.name = 'OrganisationApiError';
    }
}

/** Le message d'une erreur dont le serveur n'a rien dit (exporté : un appelant le distingue d'un message relayé). */
export const ORGANISATION_MESSAGE_GENERIQUE = 'Cette action n\'a pas pu être faite. Réessayez plus tard.';
const GENERIQUE = ORGANISATION_MESSAGE_GENERIQUE;
const RESEAU = 'Connexion impossible. Vérifiez votre réseau et réessayez.';

const config = (accessToken: string, params?: Record<string, string>): AxiosRequestConfig => ({
    headers: { Authorization: `Bearer ${accessToken}`, AccessToken: accessToken, 'Content-Type': 'application/json' },
    ...(params && Object.keys(params).length > 0 ? { params } : {}),
});

/** Le refus du serveur en erreur typée ; un 404 devient `INTROUVABLE` (la porte opaque). */
function versErreur(error: any): OrganisationApiError {
    const status: number | undefined = error?.response?.status;
    const data = error?.response?.data;
    if (!error?.response) return new OrganisationApiError('RESEAU', RESEAU);
    const code = typeof data?.code === 'string' ? data.code : status === 404 ? 'INTROUVABLE' : 'ERREUR';
    const message = typeof data?.message === 'string' && data.message ? data.message : GENERIQUE;
    return new OrganisationApiError(code, message, status);
}

async function appel<T>(fn: () => Promise<{ data: { data: T } }>): Promise<T> {
    try {
        return (await fn()).data.data;
    } catch (error) {
        throw versErreur(error);
    }
}

/** Même appel, mais un 404 (pas membre, rôle insuffisant, annuaire fermé) rend `null` en silence. */
async function appelOuNull<T>(fn: () => Promise<{ data: { data: T } }>): Promise<T | null> {
    try {
        return (await fn()).data.data;
    } catch (error) {
        const e = versErreur(error);
        if (e.status === 404) return null;
        throw e;
    }
}

async function suppression(url: string, accessToken: string): Promise<void> {
    try {
        await axios.delete(url, config(accessToken));
    } catch (error) {
        throw versErreur(error);
    }
}

const base = (id: string) => `/api/organisations/${encodeURIComponent(id)}`;

export const organisationAPI = {
    // ── Organisation, cercles, groupes ──
    mesOrganisations: (accessToken: string): Promise<OrganisationResume[]> =>
        appel(() => axios.get('/api/organisations', config(accessToken))),

    creer: (body: CreerOrganisationBody, accessToken: string): Promise<OrganisationResume> =>
        appel(() => axios.post('/api/organisations', body, config(accessToken))),

    /** 200 → la fiche ; 404 → `null` (pas membre), jamais un message. */
    fiche: (id: string, accessToken: string): Promise<OrganisationFiche | null> =>
        appelOuNull(() => axios.get(base(id), config(accessToken))),

    modifier: (id: string, body: ModifierOrganisationBody, accessToken: string): Promise<OrganisationFiche> =>
        appel(() => axios.put(base(id), body, config(accessToken))),

    /** La charte acceptée par la personne connectée (C26 SP3) : trace sur l'adhésion ; rend la fiche à jour. */
    accepterCharte: (id: string, accessToken: string): Promise<OrganisationFiche> =>
        appel(() => axios.post(`${base(id)}/charte`, {}, config(accessToken))),

    /** Liste ordonnée complète ; garder la `cle` des cercles existants pour ne pas perdre les membres. */
    reglerCercles: (id: string, cercles: Array<{ cle?: string; libelle: string }>, accessToken: string): Promise<OrganisationFiche> =>
        appel(() => axios.put(`${base(id)}/cercles`, { cercles }, config(accessToken))),

    reglerGroupes: (id: string, groupes: Array<{ cle?: string; libelle: string }>, accessToken: string): Promise<OrganisationFiche> =>
        appel(() => axios.put(`${base(id)}/groupes`, { groupes }, config(accessToken))),

    // ── Membres, invitations ──
    /** 404 pour un simple membre quand l'annuaire est fermé → `null`. */
    membres: (id: string, accessToken: string): Promise<MembreDto[] | null> =>
        appelOuNull(() => axios.get(`${base(id)}/membres`, config(accessToken))),

    modifierMembre: (id: string, userId: string, body: ModifierMembreBody, accessToken: string): Promise<MembreDto> =>
        appel(() => axios.put(`${base(id)}/membres/${encodeURIComponent(userId)}`, body, config(accessToken))),

    /** Retirer un membre, ou quitter si c'est soi. */
    retirerMembre: (id: string, userId: string, accessToken: string): Promise<void> =>
        suppression(`${base(id)}/membres/${encodeURIComponent(userId)}`, accessToken),

    invitations: (id: string, accessToken: string): Promise<InvitationDto[]> =>
        appel(() => axios.get(`${base(id)}/invitations`, config(accessToken))),

    creerInvitation: (id: string, body: CreerInvitationBody, accessToken: string): Promise<InvitationDto> =>
        appel(() => axios.post(`${base(id)}/invitations`, body, config(accessToken))),

    revoquerInvitation: (id: string, code: string, accessToken: string): Promise<void> =>
        suppression(`${base(id)}/invitations/${encodeURIComponent(code)}`, accessToken),

    /** Un code reçu (`POST /api/users/me/invitation`) : le profil (créé s'il manquait) et l'organisation rejointe. */
    rejoindre: async (code: string, accessToken: string): Promise<AdhesionParCode> => {
        try {
            const response = await axios.post('/api/users/me/invitation', { code }, config(accessToken));
            const organisation = response.data?.organisation;
            return { profile: response.data.data as UserProfile, ...(organisation ? { organisation: organisation as OrganisationResume } : {}) };
        } catch (error) {
            throw versErreur(error);
        }
    },

    // ── Dossiers ──
    dossiers: (id: string, accessToken: string): Promise<DossierDto[]> =>
        appel(() => axios.get(`${base(id)}/dossiers`, config(accessToken))),

    creerDossier: (id: string, body: DossierBody & { name: string }, accessToken: string): Promise<DossierDto> =>
        appel(() => axios.post(`${base(id)}/dossiers`, body, config(accessToken))),

    modifierDossier: (id: string, perimetreId: string, body: DossierBody, accessToken: string): Promise<DossierDto> =>
        appel(() => axios.put(`${base(id)}/dossiers/${encodeURIComponent(perimetreId)}`, body, config(accessToken))),

    supprimerDossier: (id: string, perimetreId: string, accessToken: string): Promise<void> =>
        suppression(`${base(id)}/dossiers/${encodeURIComponent(perimetreId)}`, accessToken),

    // ── Documents ──
    documents: (id: string, accessToken: string, dossier?: string): Promise<DocumentDto[]> =>
        appel(() => axios.get(`${base(id)}/documents`, config(accessToken, dossier ? { dossier } : undefined))),

    /** N adresses d'envoi pour un dossier ; les octets partent ensuite par `envoyerOctets`, puis `terminerDepot`. */
    preparerDepot: (id: string, perimetreId: string, fichiers: Array<{ fileName: string; mimeType?: string; size?: number }>, accessToken: string): Promise<DepotPrepare[]> =>
        appel<{ fichiers: DepotPrepare[] }>(() => axios.post(`${base(id)}/documents`, { perimetreId, fichiers }, config(accessToken))).then(r => r.fichiers),

    /** Les octets, directement sur le stockage par l'adresse signée (SAS court, blob de blocs — même mécanique que le dépôt personnel). */
    envoyerOctets: async (uploadUrl: string, file: File): Promise<void> => {
        const reponse = await fetch(uploadUrl, {
            method: 'PUT',
            headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
            body: file,
        });
        if (!reponse.ok) throw new OrganisationApiError('ENVOI_IMPOSSIBLE', `Le document « ${file.name} » n'a pas pu être envoyé (${reponse.status}).`, reponse.status);
    },

    terminerDepot: (id: string, fileId: string, accessToken: string): Promise<DocumentDto> =>
        appel<{ document: DocumentDto }>(() => axios.post(`${base(id)}/documents/${encodeURIComponent(fileId)}/complete`, {}, config(accessToken))).then(r => r.document),

    modifierDocument: (id: string, fileId: string, body: DocumentBody, accessToken: string): Promise<DocumentDto> =>
        appel(() => axios.put(`${base(id)}/documents/${encodeURIComponent(fileId)}`, body, config(accessToken))),

    supprimerDocument: (id: string, fileId: string, accessToken: string): Promise<void> =>
        suppression(`${base(id)}/documents/${encodeURIComponent(fileId)}`, accessToken),

    etatIndexation: (id: string, accessToken: string): Promise<EtatIndexationDto[]> =>
        appel(() => axios.get(`${base(id)}/indexation`, config(accessToken))),

    /** Le lot de préparation ; il avance ensuite par `POST /api/bulk-jobs/{jobId}/advance` (`useIndexationLot`). */
    lancerIndexation: (id: string, fileIds: string[], accessToken: string): Promise<LotIndexationCree> =>
        appel(() => axios.post(`${base(id)}/indexation`, { fileIds }, config(accessToken))),

    // ── Travaux ──
    travaux: (id: string, accessToken: string, dossier?: string): Promise<TravailDto[]> =>
        appel(() => axios.get(`${base(id)}/travaux`, config(accessToken, dossier ? { dossier } : undefined))),

    creerTravail: (id: string, body: CreerTravailBody, accessToken: string): Promise<TravailDto> =>
        appel(() => axios.post(`${base(id)}/travaux`, body, config(accessToken))),

    modifierTravail: (id: string, noteId: string, body: ModifierTravailBody, accessToken: string): Promise<TravailDto> =>
        appel(() => axios.put(`${base(id)}/travaux/${encodeURIComponent(noteId)}`, body, config(accessToken))),

    supprimerTravail: (id: string, noteId: string, accessToken: string): Promise<void> =>
        suppression(`${base(id)}/travaux/${encodeURIComponent(noteId)}`, accessToken),

    // ── Vocabulaire ──
    vocabulaire: (id: string, accessToken: string): Promise<VocabulaireDto> =>
        appel(() => axios.get(`${base(id)}/vocabulaire`, config(accessToken))),

    creerConcept: (id: string, body: ConceptBody & { cle: string }, accessToken: string): Promise<ConceptDto> =>
        appel(() => axios.post(`${base(id)}/vocabulaire/concepts`, body, config(accessToken))),

    modifierConcept: (id: string, entreeId: string, body: ConceptBody, accessToken: string): Promise<ConceptDto> =>
        appel(() => axios.put(`${base(id)}/vocabulaire/concepts/${encodeURIComponent(entreeId)}`, body, config(accessToken))),

    supprimerConcept: (id: string, entreeId: string, accessToken: string): Promise<void> =>
        suppression(`${base(id)}/vocabulaire/concepts/${encodeURIComponent(entreeId)}`, accessToken),

    /** Le concept d'une organisation vu d'Anagogia (C26 SP4) : 404 (plus membre, concept disparu) → `null`. */
    apercuConcept: (id: string, entreeId: string, accessToken: string): Promise<ApercuConceptDto | null> =>
        appelOuNull(() => axios.get(`${base(id)}/vocabulaire/concepts/${encodeURIComponent(entreeId)}/apercu`, config(accessToken))),

    faceAFaceConcept: (id: string, entreeId: string, accessToken: string): Promise<FaceAFaceDto | null> =>
        appelOuNull(() => axios.get(`${base(id)}/vocabulaire/concepts/${encodeURIComponent(entreeId)}/face-a-face`, config(accessToken))),

    // ── Garder depuis Azaria (C26 SP4) : membre ──
    garderPassage: (id: string, corps: GarderPassageCorps, accessToken: string): Promise<GarderPassageResult> =>
        appel(() => axios.post(`${base(id)}/garder/passage`, corps, config(accessToken))),

    garderReponse: (id: string, corps: GarderReponseCorps, accessToken: string): Promise<GarderReponseResult> =>
        appel(() => axios.post(`${base(id)}/garder/reponse`, corps, config(accessToken))),

    // ── Verser (C26 SP4) : éditeur ──
    /** Note ou concept : 201 et le bilan. Dossier : 202 et `{ jobId }` (`estVersementEnLot` les distingue). 409 `CONCEPT_EXISTANT`, `DOSSIER_EXISTANT` : relayés avec leur code. */
    verser: (id: string, corps: VerserCorps, accessToken: string): Promise<VersementResult | VersementEnLot> =>
        appel(() => axios.post(`${base(id)}/versements`, corps, config(accessToken))),
    // ── C26 SP5 : assistant, clé propre, abonnement, export, suppression (administrateur) ──
    assistant: (id: string, accessToken: string): Promise<AssistantOrganisationDto> =>
        appel(() => axios.get(`${base(id)}/assistant`, config(accessToken))),

    poserCle: (id: string, body: CleOrganisationBody, accessToken: string): Promise<AssistantOrganisationDto> =>
        appel(() => axios.put(`${base(id)}/assistant/cle`, body, config(accessToken))),

    retirerCle: (id: string, accessToken: string): Promise<AssistantOrganisationDto> =>
        appel(() => axios.delete(`${base(id)}/assistant/cle`, config(accessToken))),

    /** Une adresse vers laquelle envoyer le navigateur : acheter le forfait Plus (`checkout`) ou gérer l'abonnement (`portal`). */
    abonnement: (id: string, geste: 'checkout' | 'portal', accessToken: string): Promise<{ url: string; portal?: boolean }> =>
        appel(() => axios.post(`${base(id)}/abonnement/${geste}`, {}, config(accessToken))),

    /** L'archive complète (zip) et son nom, lu dans l'en-tête de la réponse. */
    exporter: async (id: string, accessToken: string): Promise<{ blob: Blob; nom: string }> => {
        try {
            const response = await axios.get(`${base(id)}/export`, { ...config(accessToken), responseType: 'blob' });
            const disposition: string = response.headers?.['content-disposition'] ?? '';
            const nom = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'organisation.zip';
            return { blob: response.data as Blob, nom };
        } catch (error) {
            throw versErreur(error);
        }
    },

    /** La suppression, le nom retapé dans le corps ; 400 CONFIRMATION_INVALIDE, 409 ABONNEMENT_ACTIF relayés. */
    supprimer: (id: string, nom: string, accessToken: string): Promise<SuppressionOrganisationResultat> =>
        appel(() => axios.post(`${base(id)}/suppression`, { nom }, config(accessToken))),

    // ── C26 SP6 : le suivi (lecture : administrateur ; ouverture : membre) ──
    suivi: (id: string, periode: PeriodeSuivi, accessToken: string): Promise<SuiviOrganisationDto> =>
        appel(() => axios.get(`${base(id)}/suivi`, config(accessToken, { periode }))),

    /** Une citation ouverte dans la façade (204) : ni le document ni la personne ne sont envoyés. L'appelant l'ignore en cas d'échec. */
    signalerOuverture: async (id: string, accessToken: string): Promise<void> => {
        try {
            await axios.post(`${base(id)}/suivi/ouverture`, {}, config(accessToken));
        } catch (error) {
            throw versErreur(error);
        }
    },

};
